import React, { useState, useEffect } from "react";
import { FaCheckCircle, FaTimesCircle, FaClock, FaClipboardCheck, FaChevronRight, FaTimes, FaHeart, FaTrophy } from "react-icons/fa";
import CONFIG from "../config";
import { toast } from "react-toastify";
import "./TestModal.css";

function TestModal({ mavzuId, mavzuNomi, onClose, onPass, userData }) {
    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [answers, setAnswers] = useState({}); // { q_id: 'a' }
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [result, setResult] = useState(null);
    const [warningCount, setWarningCount] = useState(0);
    const [isClosing, setIsClosing] = useState(false);

    useEffect(() => {
        setIsClosing(false);
    }, [mavzuId]);

    const strikeInProcess = React.useRef(false);

    // Cheat Detection Logic
    useEffect(() => {
        if (loading || result || error || questions.length === 0) return;

        const handleCheat = async (e) => {
            if (strikeInProcess.current) return;

            // Trigger on tab switch OR window blur (app switch)
            if (document.hidden || e.type === 'blur') {
                strikeInProcess.current = true;

                setWarningCount(prev => {
                    const nextWarnings = prev + 1;

                    if (nextWarnings < 3) {
                        toast.warning(`Diqqat! Test yozish vaqtida boshqa dasturga o'tish mumkin emas. Ogohlantirish: ${nextWarnings}/2`, {
                            position: "top-center",
                            autoClose: 5000,
                            className: 'cheat-toast',
                        });
                        // Unlock detection after a delay
                        setTimeout(() => { strikeInProcess.current = false; }, 2000);
                    } else {
                        // Strike 3 - Lockout
                        handleLockout();
                    }
                    return nextWarnings;
                });
            }
        };

        const handleLockout = async () => {
            setIsSubmitting(true);
            try {
                const token = localStorage.getItem("loyiha_token");
                const res = await fetch(`${CONFIG.API_BASE_URL}/lock_cheat.php?token=${token}`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({ mavzu_id: mavzuId })
                }).then(r => r.json());

                if (res.status === 'success') {
                    toast.error(`Cheat detected! Siz ${res.wait_minutes} daqiqaga bloklandingiz.`, {
                        position: "top-center",
                        autoClose: false,
                    });
                    handleClose();
                }
            } catch (err) {
                toast.error("Bloklashda xatolik yuz berdi");
                strikeInProcess.current = false;
            } finally {
                setIsSubmitting(false);
            }
        };

        document.addEventListener("visibilitychange", handleCheat);
        window.addEventListener("blur", handleCheat);
        return () => {
            document.removeEventListener("visibilitychange", handleCheat);
            window.removeEventListener("blur", handleCheat);
        };
    }, [loading, result, error, questions.length, mavzuId, onClose]);


    const [startTime, setStartTime] = useState(null);

    useEffect(() => {
        const fetchQuestions = async () => {
            try {
                const token = localStorage.getItem("loyiha_token");
                const res = await fetch(`${CONFIG.API_BASE_URL}/get_tests.php?mavzu_id=${mavzuId}&token=${token}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                }).then(r => r.json());

                if (res.status === 'success') {
                    setQuestions(res.data);
                    setStartTime(Date.now());
                } else {
                    if (res.type === 'cooldown') {
                        toast.error(res.message);
                        handleClose();
                        return;
                    }
                    setError(res.message);
                }
            } catch (err) {
                setError("Testlarni yuklashda xatolik yuz berdi");
            } finally {
                setLoading(false);
            }
        };
        fetchQuestions();
    }, [mavzuId]);

    const handleAnswer = (qId, option) => {
        setAnswers(prev => ({ ...prev, [qId]: option }));
    };

    const handleSubmit = async () => {
        if (Object.keys(answers).length < questions.length) {
            toast.warning("Barcha savollarga javob bering!");
            return;
        }

        const duration = Math.floor((Date.now() - startTime) / 1000); // seconds

        setIsSubmitting(true);
        try {
            const token = localStorage.getItem("loyiha_token");
            const res = await fetch(`${CONFIG.API_BASE_URL}/submit_test.php?token=${token}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    mavzu_id: mavzuId,
                    vaqt: duration,
                    answers: Object.keys(answers).map(id => ({ id, answer: answers[id] }))
                })
            }).then(r => r.json());

            if (res.status === 'success') {
                setResult(res);
                if (res.is_passed) {
                    toast.success("Tabriklaymiz! Testdan muvaffaqiyatli o'tdingiz.");
                    if (onPass) onPass();
                } else {
                    toast.error(`Afsus, siz ${res.score}/${res.total} to'pladingiz. Kutish vaqti: ${res.next_attempt_wait} daqiqa.`);
                }
            } else {
                toast.error(res.message);
            }
        } catch (err) {
            toast.error("Natijani yuborishda xatolik");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        setIsClosing(true);
        setTimeout(() => {
            onClose();
            setIsClosing(false);
        }, 300);
    };

    if (loading) return (
        <div className={`test-modal-overlay ${isClosing ? 'modal-overlay-fade-out' : 'modal-overlay-fade-in'}`}>
            <div className={`test-modal-card loading ${isClosing ? 'modal-content-pop-out' : 'modal-content-pop-in'}`}>
                <h3>Yuklanmoqda...</h3>
            </div>
        </div>
    );

    if (error) {
        const isNotFound = error.toLowerCase().includes("topilmadi");

        return (
            <div className={`test-modal-overlay ${isClosing ? 'modal-overlay-fade-out' : 'modal-overlay-fade-in'}`}>
                <div className={`test-modal-card error ${isNotFound ? 'error-not-found' : ''} ${isClosing ? 'modal-content-pop-out' : 'modal-content-pop-in'}`}>
                    <div className="error-icon-wrapper">
                        {isNotFound ? (
                            <FaClipboardCheck className="not-found-icon pulse-slow" />
                        ) : (
                            <FaTimesCircle className="error-icon" />
                        )}
                    </div>
                    <h3>{isNotFound ? "Testlar tayyorlanmoqda" : "Xatolik yuz berdi"}</h3>
                    <p className="error-message">
                        {isNotFound
                            ? "Ushbu mavzu bo'yicha testlar hozircha mavjud emas. Tezp orada qo'shiladi!"
                            : error}
                    </p>
                    <button onClick={handleClose} className="close-btn-premium">
                        <span>Tushunarli</span>
                        <FaChevronRight />
                    </button>
                </div>
            </div>
        );
    }

    if (result) {
        const isPerfect = result.score === result.total;

        return (
            <div className={`test-modal-overlay ${isClosing ? 'modal-overlay-fade-out' : 'modal-overlay-fade-in'}`}>
                <div className={`test-modal-card result ${result.is_passed ? (isPerfect ? 'result-perfect' : 'result-success') : 'result-fail'} ${isClosing ? 'modal-content-pop-out' : 'modal-content-pop-in'}`}>

                    {/* Special Effects for Perfect Score */}
                    {isPerfect && (
                        <div className="fireworks-container">
                            {/* Generate 20 firework divs at random positions */}
                            {[...Array(20)].map((_, i) => (
                                <div key={`fw-${i}`} className="firework" style={{
                                    left: `${Math.random() * 100}%`,
                                    top: `${Math.random() * 100}%`,
                                    backgroundColor: `hsl(${Math.random() * 360}, 100%, 50%)`,
                                    animationDelay: `${Math.random() * 1}s`
                                }} />
                            ))}
                            {/* Floating Likes */}
                            {[...Array(10)].map((_, i) => (
                                <FaHeart key={`like-${i}`} className="floating-like" style={{
                                    left: `${Math.random() * 90}%`,
                                    animationDuration: `${2 + Math.random() * 2}s`,
                                    animationDelay: `${Math.random()}s`
                                }} />
                            ))}
                        </div>
                    )}

                    <div className="result-icon-wrapper">
                        {isPerfect ? (
                            <FaTrophy className="perfect-icon" />
                        ) : result.is_passed ? (
                            <FaCheckCircle className="success-icon" />
                        ) : (
                            <FaTimesCircle className="fail-icon" />
                        )}
                    </div>

                    <h3>{isPerfect ? "Mukammal Natija!" : result.is_passed ? "Test Yakunlandi" : "Afsus, o'ta olmadingiz"}</h3>

                    <p className="score-text">Natija: <strong>{result.score} / {result.total}</strong></p>

                    {result.is_passed ? (
                        <p className="success-msg">
                            {isPerfect
                                ? "Tasanno! Barcha savollarga to'g'ri javob berdingiz. Siz haqiqiy bilimdonsiz!"
                                : "Ofarin! Siz ushbu mavzuni yaxshi o'zlashtirdingiz. Endi keyingi mavzuga o'tishingiz mumkin."}
                        </p>
                    ) : (
                        <div className="fail-msg">
                            <p>Testdan o'tish uchun kamida 4 ta to'g'ri javob kerak. Yana tayyorgarlik ko'rib urinib ko'ring.</p>
                            <div className="cooldown-box">
                                <FaClock />
                                <span>Navbatdagi urinish <strong>{result.next_attempt_wait} daqiqa</strong>dan so'ng.</span>
                            </div>
                        </div>
                    )}

                    <button onClick={handleClose} className="finish-btn">Davom etish</button>
                </div>
            </div>
        );
    }

    if (!isClosing && !questions.length) return null;

    return (
        <div className={`test-modal-overlay ${isClosing ? 'modal-overlay-fade-out' : 'modal-overlay-fade-in'}`}>
            <div className={`test-modal-card quiz ${isClosing ? 'modal-content-pop-out' : 'modal-content-pop-in'}`}>
                <div className="quiz-header">
                    <h3>{mavzuNomi} - Mavzu yuzasidan test</h3>
                    <button className="close-x" onClick={handleClose}><FaTimes /></button>
                </div>

                <div className="quiz-body">
                    {questions.map((q, index) => (
                        <div key={q.id} className="question-item">
                            <p className="question-text"><strong>{index + 1}.</strong> {q.savol_matni}</p>
                            <div className="options-grid">
                                {['a', 'b', 'c', 'd'].map(opt => (
                                    <label key={opt} className={`option-label ${answers[q.id] === opt ? 'selected' : ''}`}>
                                        <input
                                            type="radio"
                                            name={`q_${q.id}`}
                                            value={opt}
                                            onChange={() => handleAnswer(q.id, opt)}
                                            checked={answers[q.id] === opt}
                                        />
                                        <span className="opt-letter">{opt.toUpperCase()}</span>
                                        <span className="opt-text">{q['variant_' + opt]}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>

                <div className="quiz-footer">
                    <p className="progress-info">{Object.keys(answers).length} / {questions.length} belgilandi</p>
                    <button className="submit-test-btn" onClick={handleSubmit} disabled={isSubmitting}>
                        {isSubmitting ? "Yuborilmoqda..." : <><FaChevronRight /> Yakunlash</>}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default TestModal;
